import { createContext, useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from './useAuth'
import companyTranslations from '../modules/i18n/locales/en-company.json'
import { TOptions } from 'i18next'
import type { UserService } from '@duck/api-definitions'

// Type for the translation object structure
type PartnerTranslationObject = typeof companyTranslations

// Type for all possible translation keys
export type PartnerTranslationKey = keyof PartnerTranslationObject

/**
 * Org type of the organization being VIEWED when it isn't the viewer's own —
 * e.g. an admin browsing a partner org's dashboard or cohort page. Pages that
 * render another org's data provide this so every tP call in their subtree
 * resolves to that org's vocabulary instead of falling back to "company".
 */
export const PartnerOrgTypeContext = createContext<
  UserService.OrganizationType | undefined
>(undefined)

/**
 * Type-safe translation hook for partner-specific terminology.
 * Uses organizationType as the namespace (e.g. company, school), defaulting to "company".
 * Resolution order: explicit override → PartnerOrgTypeContext → viewer's own org type.
 *
 * @example
 * ```tsx
 * const { tP } = usePartnerTar();
 *
 * // Type-safe usage
 * const newText = tP('newCohort'); // ✅ Valid - returns "New Position" or "New Class"
 * const invalidText = tP('invalidKey'); // ❌ TypeScript error
 * ```
 */
export function usePartnerTar(
  organizationTypeOverride?: UserService.OrganizationType
) {
  const { organizationType } = useAuth()
  const viewedOrganizationType = useContext(PartnerOrgTypeContext)
  const namespace =
    organizationTypeOverride ?? viewedOrganizationType ?? organizationType ?? 'company'

  const { t: i18nT, i18n } = useTranslation(namespace)

  /**
   * Type-safe translation function.
   * Passes ns and lng explicitly so the correct namespace and language are used
   * after changeLanguage (avoids react-i18next using wrong namespace on language switch).
   */
  const tP = useCallback(
    <K extends PartnerTranslationKey>(key: K, options?: TOptions): string => {
      return i18nT(key, {
        ...options,
        ns: namespace,
        lng: i18n.language,
      }) as PartnerTranslationObject[K]
    },
    [i18nT, i18n.language, namespace]
  )

  return {
    tP,
  }
}
